// Import caption-related types from contentlab types
import type {DraftAiAvatarProject} from '@contentlab/stores/contentLabStore';
import type {
  Alignment,
  Animation,
  VerticalPosition,
  Typography,
  TextStroke,
  Styling,
  StyleConfig,
  CaptionsStylePreset,
} from '@contentlab/types/captions';

export interface TranscriptionWord {
  word: string;
  start: number;
  end: number;
}

export interface TranscriptionSegment {
  start: number;
  end: number;
  text: string;
}

export interface TranscriptionData {
  language: string;
  segments: TranscriptionSegment[];
  words: TranscriptionWord[] | null;
}

export function mapWordsToSegments(
  transcriptionData: TranscriptionData
): TranscriptionWord[][] | undefined {
  if (!transcriptionData.words || transcriptionData.words.length === 0) {
    return undefined;
  }

  const wordsInSegments: TranscriptionWord[][] = transcriptionData.segments.map(
    () => []
  );

  let wordIdx = 0;

  for (
    let segmentIdx = 0;
    segmentIdx < transcriptionData.segments.length;
    segmentIdx += 1
  ) {
    const segment = transcriptionData.segments[segmentIdx];

    while (
      wordIdx < transcriptionData.words.length &&
      transcriptionData.words[wordIdx].start < segment.end
    ) {
      wordsInSegments[segmentIdx].push(transcriptionData.words[wordIdx]);
      wordIdx += 1;
    }
  }
  return wordsInSegments;
}

export interface LanguageResponse {
  name: string;
  code: string;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export enum ProjectType {
  DUBBING = 'dubbing',
  REFORMAT = 'reformat',
  UPSCALE = 'upscale',
  REPHRASING = 'rephrasing',
  CAPTIONS = 'captions',
  AI_AVATAR = 'ai_avatar',
}

export enum DraftProjectStatus {
  UPDATE_DRAFT = 'update_draft',
  EXECUTE = 'execute',
}

// Base types
export interface AnalyticsParams {
  interactionId: string;
  sessionId: string;
}

export interface ValidationError {
  errorCodes: string[];
}

export interface AssetValidationError {
  assetId: string;
  errorCodes: string[];
}

export interface UserInputValidationErrors {
  assets?: AssetValidationError[];
}

export interface ConfigurationValidationErrors {
  errorCodes?: string[] | null;
}

export interface ValidationErrors {
  userInput?: UserInputValidationErrors;
  configuration?: ConfigurationValidationErrors;
}

export interface BaseProjectResponse {
  projectId: string;
  projectName: string;
  type: ProjectType;
  validationErrors?: ValidationErrors | null;
}

// Dubbing specific types
export interface DubbingSpec {
  assetId: string;
  outputLanguage: string;
  enableLipSync: boolean;
  numOfSpeakers: number;
}

export interface DubbingDraft {
  dubbingSpecs: DubbingSpec[];
}

export interface DubbingProjectResponse extends BaseProjectResponse {
  type: ProjectType.DUBBING;
  validationErrors?: ValidationErrors | null;
}

export interface ReformatSpec {
  assetId: string;
  outputAspectRatio: string;
}

export interface ReformatDraft {
  reformatSpecs: ReformatSpec[];
}

export interface UpscaleSpec {
  assetId: string;
  outputResolution: number;
}

export interface UpscaleDraft {
  upscaleSpecs: UpscaleSpec[];
}

export interface UpscaleProjectResponse extends BaseProjectResponse {
  type: ProjectType.UPSCALE;
  validationErrors?: ValidationErrors | null;
}

export interface ReformatProjectResponse extends BaseProjectResponse {
  type: ProjectType.REFORMAT;
  validationErrors?: ValidationErrors | null;
}

// Rephrasing specific types
export interface RephrasingSpec {
  assetId: string;
  outputTranscription: string | null;
  enableLipSync: boolean;
}

export interface RephrasingDraft {
  rephrasingSpec: RephrasingSpec;
}

export interface RephrasingProjectResponse extends BaseProjectResponse {
  type: ProjectType.REPHRASING;
  validationErrors?: ValidationErrors | null;
  transcriptionUrl: string;
}

export type {
  Alignment,
  Animation,
  VerticalPosition,
  Typography,
  TextStroke,
  Styling,
  StyleConfig,
  CaptionsStylePreset,
};

// Captions specific types
export interface CaptionsSpec {
  outputLanguage: string;
}

export interface CaptionsDraft {
  assetId: string;
  captionsSpecs: CaptionsSpec[];
  style: StyleConfig;
  originalLanguage: string;
  outputTranscriptionAssetId?: string | null;
  editedTranscription?: TranscriptionData | null;
}

export interface CaptionsProjectResponse extends BaseProjectResponse {
  type: ProjectType.CAPTIONS;
  validationErrors: ValidationErrors | null;
  transcriptionUrl: string | null;
  language: {
    name: string;
    code: string;
  } | null;
}

// AI Avatar specific types (client-side request typing)
export interface AiAvatarScriptItem {
  script: string;
}

export interface AiAvatarScriptData {
  scripts: AiAvatarScriptItem[];
  selectedScriptIndex: number;
}

export interface AiAvatarDraft {
  assetId: string;
  scriptData: AiAvatarScriptData | null;
  // Keep avatarSpec flexible; server validates exact shape
  avatarSpec: Partial<DraftAiAvatarProject['avatarSpec']>;
}

// Request types
export interface CreateDraftProjectParams {
  projectName: string;
  type: ProjectType;
  draft:
    | DubbingDraft
    | ReformatDraft
    | RephrasingDraft
    | CaptionsDraft
    | AiAvatarDraft
    | UpscaleDraft;
}

export interface UpdateDraftProjectParams {
  status: DraftProjectStatus;
  projectName: string;
  type: ProjectType;
  draft:
    | DubbingDraft
    | ReformatDraft
    | RephrasingDraft
    | CaptionsDraft
    | AiAvatarDraft
    | UpscaleDraft;
  analyticsParams: AnalyticsParams;
}

export interface UpdateDraftProjectResponse {
  type: ProjectType;
  validationErrors?: ValidationErrors | null;
}

/**
 * Parameters for updating a project using a PATCH request.
 */
export interface UpdateProjectPatchParams {
  /** New name for the project. */
  projectName: string;
}
